import { useSearchParams } from 'react-router-dom';
import React, { useState, useEffect, useRef } from 'react';
import { FiPlus, FiTrash2, FiPrinter, FiSend, FiX } from 'react-icons/fi';
import DashboardLayout from '../components/DashboardLayout.js';
import { fetchEvents, fetchBudgetLines, createBudgetLine, deleteBudgetLine, sendBudgetEmail } from '../apiService';
import './Budget.css';

const BUDGET_CATEGORIES = ['Booth', 'Travel', 'Booth Expenses', 'Marketing Expenses'];

function Budget() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [events, setEvents] = useState([]);
  // The sidebar links to a specific show, so honour ?eventId= when present
  // rather than always defaulting to the first event.
  const [searchParams, setSearchParams] = useSearchParams();
  const eventIdFromUrl = searchParams.get('eventId');
  const [selectedEventId, setSelectedEventId] = useState(eventIdFromUrl || '');
  const [budgetItems, setBudgetItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newBudgetItem, setNewBudgetItem] = useState({ category: 'Travel', itemName: '', description: '', budgetCost: '', actualCost: '', dueDate: '' });

  const [showSendDialog, setShowSendDialog] = useState(false);
  const [recipients, setRecipients] = useState(['']);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  useEffect(() => {
    async function loadEvents() {
      if (!user.userId) { setLoading(false); return; }
      const result = await fetchEvents(user.userId);
      if (result.success) {
        setEvents(result.data);
        if (result.data.length > 0) {
          const fromUrl = result.data.find(e => e.id === eventIdFromUrl);
          setSelectedEventId(fromUrl ? fromUrl.id : result.data[0].id);
        }
      }
      setLoading(false);
    }
    loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedEventId) {
      loadItems(selectedEventId);
    } else {
      setBudgetItems([]);
    }
  }, [selectedEventId]);

  async function loadItems(eventId) {
    const result = await fetchBudgetLines({ eventId });
    if (result.success) setBudgetItems(result.data || []);
  }

  const selectedEvent = events.find(e => e.id === selectedEventId);

  // The category "+" buttons sit well above the add form, so clicking one
  // used to have no visible effect. Scrolling and focusing makes the link
  // between the button and the form obvious.
  const addFormRef = useRef(null);
  const itemNameRef = useRef(null);

  function startAddingItem(category) {
    setNewBudgetItem({ ...newBudgetItem, category });
    setTimeout(() => {
      addFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      itemNameRef.current?.focus();
    }, 50);
  }

  async function addBudgetItem() {
    if (!newBudgetItem.itemName || !selectedEventId) return;
    const result = await createBudgetLine({ eventId: selectedEventId, ...newBudgetItem });
    if (result.success) {
      setNewBudgetItem({ category: 'Travel', itemName: '', description: '', budgetCost: '', actualCost: '', dueDate: '' });
      loadItems(selectedEventId);
    }
  }

  async function removeBudgetItem(id) {
    await deleteBudgetLine(id);
    loadItems(selectedEventId);
  }

  function getItemsByCategory(category) {
    return budgetItems.filter(item => item.category === category);
  }

  function getBudgetTotal() {
    return budgetItems.reduce((sum, item) => sum + (parseFloat(item.budgetCost) || 0), 0);
  }

  function getActualTotal() {
    return budgetItems.reduce((sum, item) => sum + (parseFloat(item.actualCost) || 0), 0);
  }

  function openSendDialog() {
    setRecipients([user.email || '']);
    setSendResult(null);
    setShowSendDialog(true);
  }

  function updateRecipient(index, value) {
    const next = [...recipients];
    next[index] = value;
    setRecipients(next);
  }

  function addRecipientField() {
    setRecipients([...recipients, '']);
  }

  function removeRecipientField(index) {
    setRecipients(recipients.filter((_, i) => i !== index));
  }

  async function handleSend() {
    const cleaned = recipients.map(r => r.trim()).filter(Boolean);
    if (cleaned.length === 0) {
      setSendResult({ ok: false, msg: 'Enter at least one email address.' });
      return;
    }
    setSending(true);
    setSendResult(null);
    const result = await sendBudgetEmail(selectedEventId, cleaned);
    setSending(false);
    if (result.success) {
      setSendResult({ ok: true, msg: `Sent to ${result.sent} recipient${result.sent === 1 ? '' : 's'}.` });
    } else {
      setSendResult({ ok: false, msg: result.error || 'Failed to send.' });
    }
  }

  return (
    <DashboardLayout>
      <h1 className="page-title">
        Trade Show Budget
        {selectedEvent && (
          <span className="page-title-context">{' \u2014 '}{selectedEvent.eventName}</span>
        )}
      </h1>

      <div className="budget-event-select">
        <label>Event:</label>
        <select value={selectedEventId} onChange={e => {
          setSelectedEventId(e.target.value);
          if (e.target.value) {
            setSearchParams({ eventId: e.target.value });
          } else {
            setSearchParams({});
          }
        }}>
          {events.length === 0 && <option value="">No events yet</option>}
          {events.map(ev => (
            <option key={ev.id} value={ev.id}>{ev.eventName}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="eval-card"><p>Loading...</p></div>
      ) : !selectedEvent ? (
        <div className="eval-card"><p>Create an event first, then track its budget here.</p></div>
      ) : (
        <div className="eval-card">
          <div className="eval-budget-header">
            <div><strong>Booth No. &amp; Size:</strong> {selectedEvent.boothSize || '—'}</div>
            <div><strong>Exhibit Dates:</strong> {selectedEvent.exhibitStartDate || selectedEvent.eventDate || '—'}</div>
            <div><strong>Location:</strong> {selectedEvent.venue || '—'}</div>
          </div>

          {BUDGET_CATEGORIES.map(category => (
            <div key={category} className="eval-budget-section">
              <div className="eval-budget-section-header">
                <h3>{category.toUpperCase()}:</h3>
                <button className="eval-add-line-btn" title={`Add an item to ${category}`} onClick={() => startAddingItem(category)}>
                  <FiPlus size={14} />
                </button>
              </div>
              <table className="eval-budget-table">
                <thead>
                  <tr>
                    <th>ITEM</th>
                    <th>DESCRIPTION</th>
                    <th>BUDGET COST ($)</th>
                    <th>ACTUAL COST ($)</th>
                    <th>DUE DATE</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {getItemsByCategory(category).map(item => (
                    <tr key={item.id}>
                      <td>{item.itemName}</td>
                      <td>{item.description}</td>
                      <td>${parseFloat(item.budgetCost || 0).toLocaleString()}</td>
                      <td>{item.actualCost ? `$${parseFloat(item.actualCost).toLocaleString()}` : ''}</td>
                      <td>{item.dueDate || ''}</td>
                      <td>
                        <button className="eval-icon-btn delete" onClick={() => removeBudgetItem(item.id)}><FiTrash2 size={14} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}

          <div className="eval-add-item-form" ref={addFormRef}>
            <h4>Add Budget Item</h4>
            <div className="eval-add-item-row">
              <select value={newBudgetItem.category} onChange={e => setNewBudgetItem({ ...newBudgetItem, category: e.target.value })}>
                {BUDGET_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <input ref={itemNameRef} type="text" value={newBudgetItem.itemName} onChange={e => setNewBudgetItem({ ...newBudgetItem, itemName: e.target.value })} placeholder="Item name" />
              <input type="text" value={newBudgetItem.description} onChange={e => setNewBudgetItem({ ...newBudgetItem, description: e.target.value })} placeholder="Description" />
              <input type="number" value={newBudgetItem.budgetCost} onChange={e => setNewBudgetItem({ ...newBudgetItem, budgetCost: e.target.value })} placeholder="Budget $" />
              <input type="number" value={newBudgetItem.actualCost} onChange={e => setNewBudgetItem({ ...newBudgetItem, actualCost: e.target.value })} placeholder="Actual $" />
              <input type="date" value={newBudgetItem.dueDate} onChange={e => setNewBudgetItem({ ...newBudgetItem, dueDate: e.target.value })} />
              <button className="eval-add-btn" onClick={addBudgetItem}><FiPlus size={14} /> Add</button>
            </div>
          </div>

          <div className="eval-budget-totals">
            <div className="eval-total-row"><span>Total (Budget):</span><strong>${getBudgetTotal().toLocaleString()}</strong></div>
            <div className="eval-total-row"><span>Total (Actual):</span><strong>${getActualTotal().toLocaleString()}</strong></div>
          </div>

          <div className="eval-budget-actions">
            <button className="eval-print-btn" onClick={() => window.print()}>
              <FiPrinter size={16} /> Print Budget Show
            </button>
            <button className="eval-send-btn" onClick={openSendDialog}>
              <FiSend size={16} /> Send Budget Show
            </button>
          </div>
        </div>
      )}

      {showSendDialog && (
        <div className="event-form-overlay">
          <div className="event-form-card" style={{ maxWidth: '480px' }}>
            <div className="form-header">
              <h2>Send Budget</h2>
              <button className="close-btn" onClick={() => setShowSendDialog(false)}><FiX size={20} /></button>
            </div>
            <p style={{ fontSize: '13px', color: '#666', marginTop: 0 }}>
              Email this budget for <strong>{selectedEvent?.eventName}</strong>. Your address is filled in by default; add others if you'd like.
            </p>
            {recipients.map((r, i) => (
              <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                <input
                  type="email"
                  value={r}
                  onChange={e => updateRecipient(i, e.target.value)}
                  placeholder="name@example.com"
                  style={{ flex: 1, padding: '8px 10px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
                />
                {recipients.length > 1 && (
                  <button className="eval-icon-btn delete" onClick={() => removeRecipientField(i)}><FiX size={16} /></button>
                )}
              </div>
            ))}
            <button
              onClick={addRecipientField}
              style={{ background: 'none', border: 'none', color: '#7c3aed', cursor: 'pointer', fontSize: '13px', padding: '4px 0', marginBottom: '12px' }}
            >
              + Add another recipient
            </button>
            {sendResult && (
              <p style={{ fontSize: '13px', color: sendResult.ok ? '#15803d' : '#dc2626', margin: '8px 0' }}>
                {sendResult.msg}
              </p>
            )}
            <div className="form-actions">
              <button type="button" className="cancel-btn" onClick={() => setShowSendDialog(false)}>Close</button>
              <button type="button" className="submit-btn" onClick={handleSend} disabled={sending}>
                {sending ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

export default Budget;
